import { useState, useMemo, useEffect } from 'react';
import { List, useListContext, CreateButton, TopToolbar, useRedirect } from 'react-admin';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TextField, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { tbl, btn, PAYMENT_STATUS, PAYMENT_METHOD_LABELS, badgeStyle, inputSx, clickableA11y } from '../styles/ds';
import { useFitCount } from '../hooks/useFitCount';
import FitPagination from '../components/FitPagination';

const fmtAmount = (amount, currency) => {
  const n = Number(amount || 0).toLocaleString('he-IL', { minimumFractionDigits: 2 });
  return currency === 'USD' ? `$${n}` : `₪${n}`;
};
const fmtDate = iso => iso
  ? new Date(iso).toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—';

const Toolbar = () => (
  <TopToolbar><CreateButton label="תשלום חדש" sx={btn.create} /></TopToolbar>
);

const PaymentTable = () => {
  const { data = [], isLoading } = useListContext();
  const redirect = useRedirect();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { fitCount, renderCount, containerRef, startIndex } = useFitCount({ fallback: 10, page });

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter(p =>
      (p.customer_name || '').toLowerCase().includes(q) ||
      String(p.order_id).includes(q) ||
      String(p.id).includes(q)
    );
  }, [data, search]);

  useEffect(() => { setPage(1); }, [search]);

  const pageItems = useMemo(() => {
    return filtered.slice(startIndex, startIndex + renderCount);
  }, [filtered, startIndex, renderCount]);

  if (isLoading) return null;

  return (
    <Box>
      <Box className="crm-list-search" px={2} py={1.5}>
        <TextField
          size="small"
          placeholder="חיפוש לפי שם לקוח או מספר הזמנה..." aria-label="חיפוש תשלומים"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment>,
            sx: { ...inputSx, direction: 'rtl' },
          }}
          sx={{ width: 320 }}
        />
      </Box>

      <TableContainer component={Box} sx={{ ...tbl.container, border: '1px solid rgba(13,29,62,0.06)' }}>
        <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="טבלה נגללת"><Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}>#</TableCell>
              <TableCell>לקוח</TableCell>
              <TableCell>הזמנה</TableCell>
              <TableCell align="right">סכום</TableCell>
              <TableCell>אמצעי תשלום</TableCell>
              <TableCell align="center">סטטוס</TableCell>
              <TableCell align="center">תאריך</TableCell>
            </TableRow>
          </TableHead>
          <TableBody ref={containerRef}>
            {pageItems.map((p, i) => (
              <TableRow key={p.id} onClick={() => redirect('edit', 'payments', p.id)}
                {...clickableA11y(() => redirect('edit', 'payments', p.id), { ariaLabel: `פתיחת תשלום מספר ${p.id}` })}
                sx={tbl.bodyRow(i)}>
                <TableCell><Typography fontWeight={600} color="text.secondary" fontSize={13}>#{p.id}</Typography></TableCell>
                <TableCell><Typography fontWeight={600} fontSize={14}>{p.customer_name || '—'}</Typography></TableCell>
                <TableCell><Typography fontSize={13} color="text.secondary">הזמנה #{p.order_id}</Typography></TableCell>
                <TableCell align="right"><Typography fontWeight={700} color="primary.main" fontSize={14}>{fmtAmount(p.amount, p.currency)}</Typography></TableCell>
                <TableCell><Typography fontSize={13} color="text.secondary">{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method || '—'}</Typography></TableCell>
                <TableCell align="center">
                  <span style={badgeStyle(PAYMENT_STATUS, p.status)}>
                    {(PAYMENT_STATUS[p.status] || { label: p.status }).label}
                  </span>
                </TableCell>
                <TableCell align="center"><Typography fontSize={13} color="text.secondary">{fmtDate(p.created_at)}</Typography></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  {search ? 'לא נמצאו תוצאות' : 'אין תשלומים עדיין'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table></Box>
      </TableContainer>
      <FitPagination page={page} setPage={setPage} total={filtered.length} perPage={fitCount} />
    </Box>
  );
};

const PaymentList = () => (
  <List className="crm-mobile-list crm-inline-list" sort={{ field: 'id', order: 'DESC' }} perPage={1000} pagination={false} storeKey={false} actions={<Toolbar />} title="תשלומים" sx={tbl.listSx}>
    <PaymentTable />
  </List>
);

export default PaymentList;
